import ToggleSwitch from '@codegouvfr/react-dsfr/ToggleSwitch';
import clsx from 'clsx';
import {
  isSettingLocked,
  type ProgrammingPlanEditionPhase
} from 'maestro-shared/schema/ProgrammingPlan/ProgrammingPlanEditionPhase';
import {
  managedKey,
  type ProgrammingPlanSettingKey,
  type ProgrammingPlanSettings
} from 'maestro-shared/schema/ProgrammingPlan/ProgrammingPlanSettings';
import type { ReactNode } from 'react';
import { assert, type Equals } from 'tsafe';
import { SettingInheritanceLockButton } from '../SettingInheritanceLockButton/SettingInheritanceLockButton';
import './ProgrammingPlanSettingInheritance.scss';
import { cx } from '@codegouvfr/react-dsfr/fr/cx';

type SettingInheritanceOptions<T extends ProgrammingPlanSettings> = {
  settingKey: ProgrammingPlanSettingKey;
  label: string;
  settings: T;
  planSettings: ProgrammingPlanSettings | undefined;
  editionPhase: ProgrammingPlanEditionPhase;
  inheritanceDisabledReason?: string;
  onChange: (settings: T) => void;
};

export const useSettingInheritance = <T extends ProgrammingPlanSettings>({
  settingKey,
  label,
  settings,
  planSettings,
  editionPhase,
  inheritanceDisabledReason,
  onChange
}: SettingInheritanceOptions<T>) => {
  const managed = settings[managedKey(settingKey)];
  const managedAtPlanLevel = planSettings?.[managedKey(settingKey)] ?? false;
  const isInherited = managedAtPlanLevel && !managed;
  const locked = isSettingLocked(editionPhase, settingKey);
  const disabledReason = locked ? null : inheritanceDisabledReason;

  const change = (patch: Partial<ProgrammingPlanSettings>) =>
    onChange({ ...settings, ...patch });

  return {
    disabled: isInherited || locked,
    isFieldVisible: planSettings !== undefined || managed,
    required: managed,
    lockButton: managedAtPlanLevel && (
      <SettingInheritanceLockButton
        isInherited={isInherited}
        title={
          disabledReason ??
          (isInherited ? 'Géré par le plan' : 'Détaché du plan')
        }
        disabled={disabledReason !== undefined}
        onClick={() =>
          change(
            isInherited
              ? { [managedKey(settingKey)]: true }
              : {
                  [managedKey(settingKey)]: false,
                  [settingKey]: planSettings?.[settingKey]
                }
          )
        }
      />
    ),
    toggle: !planSettings && (
      <ToggleSwitch
        label={null}
        labelPosition="left"
        showCheckedHint={false}
        inputTitle={`Paramétrer « ${label} » au niveau du plan`}
        disabled={disabledReason !== undefined}
        checked={managed}
        onChange={(managed) => change({ [managedKey(settingKey)]: managed })}
      />
    ),
    disabledReasonHint: (!planSettings || locked) && disabledReason && (
      <p className={cx('fr-hint-text', 'fr-mb-0')}>{disabledReason}</p>
    )
  };
};

type Props<T extends ProgrammingPlanSettings> = SettingInheritanceOptions<T> & {
  labelContent?: ReactNode;
  hintText?: string;
  children: (props: {
    disabled: boolean;
    label: ReactNode;
    required: boolean;
  }) => ReactNode;
};

export const ProgrammingPlanSettingInheritance = <
  T extends ProgrammingPlanSettings
>({
  settingKey,
  label,
  labelContent = label,
  hintText,
  settings,
  planSettings,
  editionPhase,
  inheritanceDisabledReason,
  onChange,
  children,
  ..._rest
}: Props<T>) => {
  assert<Equals<keyof typeof _rest, never>>();

  const {
    disabled,
    isFieldVisible,
    required,
    lockButton,
    toggle,
    disabledReasonHint
  } = useSettingInheritance({
    settingKey,
    label,
    settings,
    planSettings,
    editionPhase,
    inheritanceDisabledReason,
    onChange
  });

  const composedLabel = (
    <span
      className="setting-inheritance-label"
      style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
    >
      {lockButton}
      {labelContent}
    </span>
  );

  return (
    <div
      className={clsx(
        'programming-plan-setting-inheritance',
        'd-flex-row',
        isFieldVisible ? 'd-flex-align-start' : 'd-flex-align-center',
        'border',
        cx('fr-p-2w')
      )}
      style={{ gap: '0.5rem', flexWrap: 'wrap' }}
    >
      <div style={{ flex: 1 }}>
        {isFieldVisible ? (
          children({
            disabled,
            label: composedLabel,
            required
          })
        ) : (
          <span className={cx('fr-label', 'fr-label--disabled')}>
            {labelContent}
          </span>
        )}
        {disabledReasonHint}
      </div>
      {toggle}
      {isFieldVisible && hintText && (
        <p
          className={cx('fr-message', 'fr-message--info')}
          style={{ flexBasis: '100%' }}
        >
          {hintText}
        </p>
      )}
    </div>
  );
};
